import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { GlobalWorkerOptions, TextLayer, getDocument, type PDFDocumentProxy } from "pdfjs-dist";
import "pdfjs-dist/web/pdf_viewer.css";

import { ViewerZoomToolbar } from "@/components/shared/ViewerZoomToolbar";
import { useFetchPdfBytes } from "@/hooks/useFetchPdfBytes";
import { useFitToWidthZoom } from "@/hooks/useFitToWidthZoom";
import type { CitationObject } from "@/lib/stream";
import { normalizeViewerText } from "@/lib/viewerText";

GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).toString();

/**
 * Chrome (px) around the page when fitting to width. Larger than the DOCX path
 * because each PDF page renders inside a bordered, padded shell within a padded
 * stage; this reserves that chrome plus the scrollbar.
 */
const STAGE_INSET = 64;

function pageNumbers(page?: string | number) {
  if (typeof page === "number") {
    return [page];
  }
  if (!page) {
    return [];
  }
  const [start, end] = page.split("-").map((part) => Number.parseInt(part, 10)).filter(Number.isFinite);
  if (!start) {
    return [];
  }
  if (!end || end <= start) {
    return [start];
  }
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

interface RenderedPage {
  pageNumber: number;
  text: string;
}

interface DocViewProps {
  documentId: string;
  versionId?: string;
  citation?: CitationObject;
}

type ReleasablePDFDocument = PDFDocumentProxy & { destroy?: () => Promise<void> | void };

function releasePdf(pdf: PDFDocumentProxy | null) {
  if (!pdf) {
    return;
  }

  const releasable = pdf as ReleasablePDFDocument;
  if (typeof releasable.destroy === "function") {
    void releasable.destroy();
    return;
  }

  void pdf.cleanup();
}

export function DocView({ documentId, versionId, citation }: DocViewProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const naturalWidthRef = useRef(0);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [parseError, setParseError] = useState<string | null>(null);

  // ponytail: declarative hook so query invalidation (post edit accept/reject) triggers re-render
  const { bytes, loading, error } = useFetchPdfBytes(documentId, versionId);

  const { zoom, setFitZoom, fitMode, computeFitZoom, fitToWidth, setManualZoom } = useFitToWidthZoom({
    scrollRef,
    naturalWidthRef,
    stageInset: STAGE_INSET,
    refitOnResize: Boolean(pdf),
  });

  useEffect(() => {
    if (!bytes) {
      return;
    }
    let cancelled = false;
    let loadedDocument: PDFDocumentProxy | null = null;
    setParseError(null);
    void getDocument({ data: bytes.slice(0) }).promise
      .then(async (document) => {
        loadedDocument = document;
        const firstPage = await document.getPage(1);
        naturalWidthRef.current = firstPage.getViewport({ scale: 1 }).width;
        if (!cancelled) {
          setPdf(document);
        } else {
          releasePdf(document);
        }
      })
      .catch((parseErr) => {
        if (!cancelled) {
          setParseError(parseErr instanceof Error ? parseErr.message : "Could not parse the document.");
        }
      });
    return () => {
      cancelled = true;
      releasePdf(loadedDocument);
    };
  }, [bytes]);

  useEffect(() => {
    const container = containerRef.current;
    const scroll = scrollRef.current;
    if (!container || !scroll || !pdf) {
      return;
    }

    // Settle on the fit zoom before building placeholders so pages rasterize once at the right scale.
    if (fitMode) {
      const fit = computeFitZoom();
      if (fit != null && Math.abs(fit - zoom) > 0.01) {
        setFitZoom(fit);
        return;
      }
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    container.replaceChildren();
    setPages([]);

    const renderedSet = new Set<number>();

    const targetPageNums = pageNumbers(citation?.page);
    const priorityPages = targetPageNums.length > 0
      ? targetPageNums.filter((pn) => pn >= 1 && pn <= pdf.numPages)
      : [1];

    const placeholderW = naturalWidthRef.current * zoom;
    const placeholderH = placeholderW * 1.414;

    for (let i = 1; i <= pdf.numPages; i += 1) {
      const shell = document.createElement("section");
      shell.className = "rounded-md border bg-card p-3";
      shell.dataset.pageNumber = String(i);
      shell.style.width = `${placeholderW}px`;
      shell.style.minHeight = `${placeholderH}px`;
      container.appendChild(shell);
    }

    const renderPage = async (pageNumber: number): Promise<void> => {
      if (cancelled || renderedSet.has(pageNumber)) {
        return;
      }
      renderedSet.add(pageNumber);

      const page = await pdf.getPage(pageNumber);
      if (cancelled) {
        return;
      }
      const viewport = page.getViewport({ scale: zoom });
      const shell = container.querySelector<HTMLElement>(`section[data-page-number="${pageNumber}"]`);
      if (!shell) {
        return;
      }

      shell.style.width = "";
      shell.style.minHeight = "";
      shell.replaceChildren();

      const pageShell = document.createElement("div");
      pageShell.style.position = "relative";
      pageShell.style.width = `${viewport.width}px`;
      pageShell.style.height = `${viewport.height}px`;

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.className = "block";
      pageShell.appendChild(canvas);

      if (context) {
        await page.render({ canvas, canvasContext: context, viewport }).promise;
      }
      if (cancelled) {
        return;
      }

      const textContent = await page.getTextContent();
      const text = textContent.items.map((item) => ("str" in item ? item.str : "")).join(" ");

      const textLayerDiv = document.createElement("div");
      textLayerDiv.className = "textLayer pdf-text-layer";
      pageShell.appendChild(textLayerDiv);

      const tl = new TextLayer({ textContentSource: textContent, container: textLayerDiv, viewport });
      await tl.render();

      shell.appendChild(pageShell);

      setPages((prev) => [...prev, { pageNumber, text }]);
    };

    const init = async () => {
      for (const pn of priorityPages) {
        if (cancelled) {
          return;
        }
        await renderPage(pn);
      }
      if (cancelled) {
        return;
      }

      if (targetPageNums.length > 0) {
        const target = container.querySelector<HTMLElement>(`section[data-page-number="${priorityPages[0]}"]`);
        target?.scrollIntoView({ block: "start", behavior: "auto" });
        setCurrentPage(priorityPages[0]);
      }

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const pn = Number((entry.target as HTMLElement).dataset.pageNumber);
              if (pn) {
                void renderPage(pn);
                observer?.unobserve(entry.target);
              }
            }
          }
        },
        { root: scroll, rootMargin: "300px 0px" },
      );

      container.querySelectorAll<HTMLElement>("section[data-page-number]").forEach((shell) => {
        const pn = Number(shell.dataset.pageNumber);
        if (!renderedSet.has(pn)) {
          observer?.observe(shell);
        }
      });
    };

    void init();
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [citation?.page, computeFitZoom, fitMode, pdf, setFitZoom, zoom]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !citation?.quote || pages.length === 0) {
      return;
    }

    // Remove previous highlights
    container.querySelectorAll("mark").forEach((mark) => {
      const parent = mark.parentNode;
      if (parent) {
        while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
        parent.removeChild(mark);
      }
    });

    const quoteParts = citation.quote.split("[[PAGE_BREAK]]").map(normalizeViewerText).filter(Boolean);
    const targets = pageNumbers(citation.page);
    const candidatePages = targets.length ? pages.filter((page) => targets.includes(page.pageNumber)) : pages;

    for (let index = 0; index < candidatePages.length; index += 1) {
      const page = candidatePages[index];
      const textLayer = container.querySelector<HTMLElement>(`section[data-page-number="${page.pageNumber}"] .pdf-text-layer`);
      if (!textLayer) {
        continue;
      }

      const part = quoteParts[Math.min(index, quoteParts.length - 1)] ?? quoteParts[0];
      if (!normalizeViewerText(page.text).includes(part.slice(0, Math.min(120, part.length)))) {
        continue;
      }

      // Find the contiguous run of spans whose concatenated text contains the quote
      const spans = Array.from(textLayer.querySelectorAll<HTMLElement>("span"));
      const spanTexts = spans.map((s) => normalizeViewerText(s.textContent ?? ""));
      let matchStart = -1;
      let matchEnd = -1;

      // Slide a window: grow the end until the concatenation contains the quote,
      // then record the tightest range.
      for (let start = 0; start < spans.length; start += 1) {
        let concat = "";
        for (let end = start; end < spans.length; end += 1) {
          concat += (concat ? " " : "") + spanTexts[end];
          if (concat.includes(part)) {
            matchStart = start;
            matchEnd = end;
            break;
          }
          // Stop expanding if concat is already much longer than the quote
          if (concat.length > part.length + 200) break;
        }
        if (matchStart >= 0) break;
      }

      if (matchStart >= 0) {
        let firstHighlighted: HTMLElement | null = null;
        for (let i = matchStart; i <= matchEnd; i += 1) {
          const span = spans[i];
          const mark = document.createElement("mark");
          // Keep the text transparent: pdf.js text-layer spans are invisible glyphs
          // positioned over the canvas, and the UA default `mark { color: black }`
          // would otherwise paint a second visible copy of the text.
          mark.className = "rounded bg-highlight/40 text-transparent";
          while (span.firstChild) mark.appendChild(span.firstChild);
          span.appendChild(mark);
          if (!firstHighlighted) firstHighlighted = span;
        }
        firstHighlighted?.scrollIntoView({ block: "center", behavior: "smooth" });
        setCurrentPage(page.pageNumber);
        if (quoteParts.length <= 1) break;
      }
    }
  }, [citation?.page, citation?.quote, pages]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error ?? parseError) {
    return <div className="p-4 text-sm text-destructive">{error ?? parseError}</div>;
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      <ViewerZoomToolbar zoom={zoom} fitMode={fitMode} onManualZoom={setManualZoom} onFitToWidth={fitToWidth}>
        <span className="ml-auto flex items-center gap-1">
          <ChevronLeft className="size-3" />
          Page {currentPage} of {pdf?.numPages ?? 0}
          <ChevronRight className="size-3" />
        </span>
      </ViewerZoomToolbar>
      <div
        ref={scrollRef}
        className="hide-scrollbar min-h-0 min-w-0 flex-1 overflow-auto bg-muted"
        onScroll={(event) => {
          const scroll = event.currentTarget;
          const top = scroll.getBoundingClientRect().top;
          const visible = Array.from(scroll.querySelectorAll<HTMLElement>("section[data-page-number]")).find((section) => section.getBoundingClientRect().bottom > top + 100);
          if (visible?.dataset.pageNumber) {
            setCurrentPage(Number(visible.dataset.pageNumber));
          }
        }}
      >
        <div ref={containerRef} className="flex min-w-min flex-col items-center gap-4 p-4" />
      </div>
    </div>
  );
}
